import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Breadcrumb from '../components/common/Breadcrumb';
import { useToast } from '../components/contexts/ToastContext';
import { useAuth } from '../components/contexts/AuthContext';
import apiService from '../services/apiService';
import { prioridadBadge, formatEstado } from '../utils/otRenderHelpers';
import { calcularDuracionMinutos, formatDuration } from '../utils/timeHelpers';
import { downloadFile } from '../utils/downloadHelpers';
import ROUTES from '../constants/routes';
import ConfirmDialog from '../components/common/ConfirmDialog';
import useConfirmAction from '../hooks/useConfirmAction';
import '../styles/pages/detalle-ot.css';

const estadoBadgeClass = {
  programado: 'badge-info',
  en_progreso: 'badge-warning',
  completado: 'badge-success',
  cancelado: 'badge-secondary'
};

const tipoColorMap = {
  PREV: { bg: '#e8f8ec', color: '#27ae60', border: '#b7e4c7' },
  CORR: { bg: '#fde8e8', color: '#dc3545', border: '#f5c6cb' },
  PRED: { bg: '#e8f0fe', color: '#3b82f6', border: '#b6d4fe' }
};

function formatFechaLarga(fecha) {
  if (!fecha) return '-';
  return new Date(fecha).toLocaleDateString('es-CL', {
    day: '2-digit', month: 'long', year: 'numeric'
  });
}

/**
 * Página de detalle de orden de trabajo (vista técnico)
 *
 * Muestra header con info de la OT, sidebar con datos del equipo
 * y panel de actividades con progreso y evidencia.
 *
 * @returns {JSX.Element}
 */
function DetalleOrdenTrabajo() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();

  const [ot, setOt] = useState(null);
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Timer en vivo para OTs en progreso
  const [timerMinutes, setTimerMinutes] = useState(0);
  const iniciarAct = useConfirmAction();

  // Estado para término formal de OT
  const confirmTerminar = useConfirmAction();
  const [observacionesCierre, setObservacionesCierre] = useState('');
  const [terminadoExito, setTerminadoExito] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [otRes, actRes] = await Promise.all([
          apiService.getOrdenTrabajoById(id),
          apiService.getActividadesOrdenTrabajo(id)
        ]);

        if (cancelled) return;

        if (otRes.success) {
          setOt(otRes.data);
          if (otRes.data.fecha_inicio) {
            setTimerMinutes(calcularDuracionMinutos(otRes.data.fecha_inicio, new Date()));
          }
        } else {
          setError(otRes.message || 'Orden de trabajo no encontrada');
        }

        if (actRes.success) {
          setActividades(actRes.data);
        }
      } catch {
        if (!cancelled) {
          setError('Error al cargar la orden de trabajo');
          showToast('Error al cargar la orden de trabajo', 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchData();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Actualizar timer cada minuto
  useEffect(() => {
    if (!ot || ot.estado !== 'en_progreso' || !ot.fecha_inicio) return;

    const interval = setInterval(() => {
      setTimerMinutes(calcularDuracionMinutos(ot.fecha_inicio, new Date()));
    }, 60000);

    return () => clearInterval(interval);
  }, [ot]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="wf-loading">
            <i className="fas fa-spinner fa-spin" /> Cargando orden de trabajo...
          </div>
        </div>
      </>
    );
  }

  if (error || !ot) {
    return (
      <>
        <Navbar />
        <div className="main-content">
          <div className="wf-error">
            <div className="wf-error-icon"><i className="fas fa-clipboard-question" /></div>
            <p>{error || 'Orden de trabajo no encontrada'}</p>
            <button className="btn btn-primary" onClick={() => navigate(ROUTES.MIS_ORDENES_TRABAJO)}>
              <i className="fas fa-arrow-left" /> Volver a Mis Órdenes
            </button>
          </div>
        </div>
      </>
    );
  }

  // ── Handlers de término formal ──
  const handleTerminar = async () => {
    confirmTerminar.setLoading(true);
    try {
      const response = await apiService.terminarOrdenTrabajo(id, {
        observaciones_cierre: observacionesCierre || undefined
      });
      if (response.success) {
        confirmTerminar.reset();
        setTerminadoExito(true);
        setOt(prev => ({ ...prev, estado: 'completado' }));
      } else {
        showToast(response.message || 'Error al terminar la OT', 'error');
        confirmTerminar.setLoading(false);
      }
    } catch {
      showToast('Error al terminar la orden de trabajo', 'error');
      confirmTerminar.setLoading(false);
    }
  };

  const handleDescargarInforme = async () => {
    try {
      const { blob, filename } = await apiService.descargarInformeOrdenTrabajo(id);
      downloadFile(blob, filename);
    } catch {
      showToast('Error al descargar el informe', 'error');
    }
  };

  const handleCloseExito = () => {
    setTerminadoExito(false);
    setObservacionesCierre('');
  };

  // Cálculos de progreso
  const completadas = actividades.filter(a => a.estado === 'completado').length;
  const enProgreso = actividades.filter(a => a.estado === 'en_progreso').length;
  const omitidas = actividades.filter(a => a.estado === 'omitido').length;
  const pendientes = actividades.filter(a => a.estado === 'pendiente').length;
  const total = actividades.length;
  const pct = total > 0 ? Math.round((completadas / total) * 100) : 0;

  // Flag de readiness para término formal
  const todasFinalizadas = total > 0 && pendientes === 0 && enProgreso === 0;
  const listoParaTerminar = ot.estado === 'en_progreso' && todasFinalizadas;

  // Color del tipo de mantenimiento
  const tipoCode = ot.tipo_mantenimiento?.codigo || '';
  const tipoColors = tipoColorMap[tipoCode] || tipoColorMap.PREV;

  const equipoNombre = [ot.equipo?.marca, ot.equipo?.modelo].filter(Boolean).join(' ');

  // ── Handlers de inicio de actividad via modal ──
  const handleActividadClick = (act) => {
    if (act.estado === 'pendiente') {
      iniciarAct.open(act);
    } else {
      navigate(ROUTES.MIS_ORDENES_TRABAJO_ACTIVIDAD
        .replace(':id', id)
        .replace(':actividadId', act.id)
      );
    }
  };

  const handleConfirmActividad = async () => {
    const act = iniciarAct.state.item;
    iniciarAct.setLoading(true);
    try {
      const response = await apiService.cambiarEstadoActividad(id, act.id, { estado: 'en_progreso' });
      if (response.success) {
        showToast('Actividad iniciada', 'success');
        iniciarAct.reset();
        setActividades(prev => prev.map(a =>
          a.id === act.id ? { ...a, estado: 'en_progreso' } : a
        ));
        if (ot.estado === 'programado') {
          setOt(prev => ({ ...prev, estado: 'en_progreso', fecha_inicio: new Date().toISOString() }));
          setTimerMinutes(0);
        }
      } else {
        showToast(response.message || 'Error al iniciar actividad', 'error');
        iniciarAct.setLoading(false);
      }
    } catch {
      showToast('Error al iniciar la actividad', 'error');
      iniciarAct.setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="main-content">

        <Breadcrumb items={[
          { label: 'Inicio', to: ROUTES.HOME },
          { label: 'Mis OTs', to: ROUTES.MIS_ORDENES_TRABAJO },
          { label: ot.codigo_ot }
        ]} />
        {/* OT Header */}
        <header className="wf-ot-header">
          <div className="wf-ot-header-inner">
            <div className="wf-ot-header-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" strokeWidth="1.75">
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2"/>
                <rect x="9" y="3" width="6" height="4" rx="1" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="9" y1="12" x2="15" y2="12" strokeLinecap="round"/>
                <line x1="9" y1="16" x2="13" y2="16" strokeLinecap="round"/>
              </svg>
            </div>

            <div className="wf-ot-header-body">
              <h1 className="wf-ot-codigo">{ot.codigo_ot}</h1>
              <div className="wf-ot-sub">
                <span
                  className="wf-ot-type-chip"
                  style={{
                    background: tipoColors.bg,
                    color: tipoColors.color,
                    borderColor: tipoColors.border,
                    '--dot-color': tipoColors.color
                  }}
                >
                  <span style={{ width: 6, height: 6, borderRadius: '50%', background: tipoColors.color }} />
                  {tipoCode} · {ot.tipo_mantenimiento?.nombre}
                </span>
                <span className="wf-ot-equipo-label">
                  <strong>{equipoNombre}</strong> — {ot.equipo?.codigo}
                </span>
              </div>
            </div>

            <div className="wf-ot-header-badges" role="group" aria-label="Estado y prioridad">
              {ot.prioridad && (
                <span className={`badge ${prioridadBadge[ot.prioridad] || ''}`}>
                  Prioridad {ot.prioridad.charAt(0).toUpperCase() + ot.prioridad.slice(1)}
                </span>
              )}
              <span className={`badge ${estadoBadgeClass[ot.estado] || 'badge-secondary'}`}>
                {formatEstado(ot.estado)}
              </span>
              {ot.estado === 'en_progreso' && ot.fecha_inicio && (
                <span className="badge badge-secondary wf-badge-timer">
                  <svg viewBox="0 0 24 24" strokeWidth="2" aria-hidden="true">
                    <circle cx="12" cy="12" r="9"/>
                    <polyline points="12 7 12 12 15 15"/>
                  </svg>
                  {formatDuration(timerMinutes)}
                </span>
              )}
            </div>
          </div>
        </header>

        {/* 2-Column Layout */}
        <div className="wf-layout">

          {/* Sidebar */}
          <aside className="wf-sidebar" aria-label="Información del equipo y orden de trabajo">
            <div className="wf-card">
              <div className="wf-card-header">
                <div className="wf-card-header-icon yellow" aria-hidden="true">
                  <svg viewBox="0 0 24 24" strokeWidth="1.75">
                    <rect x="4" y="4" width="16" height="16" rx="2"/>
                    <rect x="9" y="9" width="6" height="6"/>
                    <line x1="9" y1="2" x2="9" y2="4"/><line x1="15" y1="2" x2="15" y2="4"/>
                    <line x1="9" y1="20" x2="9" y2="22"/><line x1="15" y1="20" x2="15" y2="22"/>
                    <line x1="2" y1="9" x2="4" y2="9"/><line x1="2" y1="15" x2="4" y2="15"/>
                    <line x1="20" y1="9" x2="22" y2="9"/><line x1="20" y1="15" x2="22" y2="15"/>
                  </svg>
                </div>
                <h2 className="wf-card-title">Equipo &amp; Detalles</h2>
              </div>

              <div className="wf-card-body">
                <div className="wf-equipo-hero">
                  <div className="wf-equipo-icon" aria-hidden="true">
                    <svg viewBox="0 0 24 24" strokeWidth="2">
                      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/>
                    </svg>
                  </div>
                  <div>
                    <div className="wf-equipo-name">{equipoNombre}</div>
                    <span className="wf-equipo-codigo">{ot.equipo?.codigo}</span>
                  </div>
                </div>

                <ul className="wf-info-list">
                  <li className="wf-info-item">
                    <div className="wf-info-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" strokeWidth="1.75">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </div>
                    <div>
                      <div className="wf-info-label">Fecha de Ejecución</div>
                      <div className="wf-info-value">{formatFechaLarga(ot.fecha_ejecucion)}</div>
                    </div>
                  </li>

                  <li className="wf-info-item">
                    <div className="wf-info-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" strokeWidth="1.75">
                        <rect x="3" y="4" width="18" height="18" rx="2"/>
                        <line x1="16" y1="2" x2="16" y2="6"/>
                        <line x1="8" y1="2" x2="8" y2="6"/>
                        <line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </div>
                    <div>
                      <div className="wf-info-label">Fecha Teórica</div>
                      <div className="wf-info-value">{formatFechaLarga(ot.fecha_programada)}</div>
                    </div>
                  </li>

                  <li className="wf-info-item">
                    <div className="wf-info-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" strokeWidth="1.75">
                        <polygon points="12 2 2 7 12 12 22 7 12 2"/>
                        <polyline points="2 17 12 22 22 17"/>
                        <polyline points="2 12 12 17 22 12"/>
                      </svg>
                    </div>
                    <div>
                      <div className="wf-info-label">Plantilla</div>
                      <div className="wf-info-value">
                        {ot.plantilla?.nombre || <span className="text-muted">Sin plantilla</span>}
                      </div>
                    </div>
                  </li>

                  <li className="wf-info-item">
                    <div className="wf-info-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" strokeWidth="1.75">
                        <polyline points="17 1 21 5 17 9"/>
                        <path d="M3 11V9a4 4 0 014-4h14"/>
                        <polyline points="7 23 3 19 7 15"/>
                        <path d="M21 13v2a4 4 0 01-4 4H3"/>
                      </svg>
                    </div>
                    <div>
                      <div className="wf-info-label">Programaciones</div>
                      <div className={`wf-info-value ${ot.programaciones_count > 0 ? 'wf-info-value--warn' : 'wf-info-value--success'}`}>
                        {ot.programaciones_count || 0} programacion{ot.programaciones_count !== 1 ? 'es' : ''}
                      </div>
                    </div>
                  </li>

                  <li className="wf-info-item">
                    <div className="wf-info-icon" aria-hidden="true">
                      <svg viewBox="0 0 24 24" strokeWidth="1.75">
                        <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
                        <circle cx="12" cy="7" r="4"/>
                      </svg>
                    </div>
                    <div>
                      <div className="wf-info-label">Técnico Responsable</div>
                      <div className="wf-info-value">
                        {ot.usuario_asignado?.nombre || <span className="text-muted">Sin asignar</span>}
                      </div>
                    </div>
                  </li>
                </ul>

                {ot.descripcion && (
                  <div className="wf-desc-section">
                    <div className="wf-desc-title">Descripción del trabajo</div>
                    <p className="wf-desc-text">{ot.descripcion}</p>
                  </div>
                )}
              </div>
            </div>
          </aside>

          {/* Checklist Panel */}
          <section aria-label="Checklist de actividades">
            <div className="wf-card">
              <div className="wf-card-header">
                <div className="wf-card-header-icon green" aria-hidden="true">
                  <svg viewBox="0 0 24 24" strokeWidth="1.75">
                    <polyline points="9 11 12 14 22 4"/>
                    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/>
                  </svg>
                </div>
                <h2 className="wf-card-title">Actividades</h2>
                <span className="badge badge-warning wf-badge-sm">{total} pasos</span>
              </div>

              {/* Progress */}
              <div className="wf-progress-block" role="region" aria-label="Progreso de actividades">
                <div className="wf-progress-header">
                  <span className="wf-progress-label">Progreso de actividades</span>
                  <span className="wf-progress-pct" aria-live="polite">
                    {completadas} / {total} — {pct}%
                  </span>
                </div>
                <div className="mant-progress-bar" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100}>
                  <div className="mant-progress-fill" style={{ width: `${pct}%` }} />
                </div>
                <div className="wf-progress-sub">
                  {completadas} completada{completadas !== 1 ? 's' : ''}
                  {enProgreso > 0 && ` · ${enProgreso} en progreso`}
                  {pendientes > 0 && ` · ${pendientes} pendiente${pendientes !== 1 ? 's' : ''}`}
                  {omitidas > 0 && ` · ${omitidas} omitida${omitidas !== 1 ? 's' : ''}`}
                </div>
              </div>

              {/* Activity list */}
              <ol className="wf-act-list" aria-label="Lista de actividades">
                {actividades.map((act) => (
                  <ActividadItem key={act.id} act={act} onSelect={handleActividadClick} />
                ))}
              </ol>

              {/* Step dots footer */}
              <div className="wf-steps-footer" aria-hidden="true">
                {actividades.map(act => (
                  <span
                    key={act.id}
                    className={`wf-step-dot ${act.estado}`}
                    title={`Paso ${act.orden}: ${act.estado}`}
                  />
                ))}
                <span className="wf-steps-hint">
                  <svg viewBox="0 0 24 24" strokeWidth="1.75" aria-hidden="true">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="8" strokeLinecap="round"/>
                    <line x1="12" y1="12" x2="12" y2="16" strokeLinecap="round"/>
                  </svg>
                  Haz clic en una actividad para expandirla
                </span>
              </div>

              {/* Barra de término formal */}
              {listoParaTerminar && hasPermission('ordenes_trabajo.terminar') && (
                <div className="wf-completion-bar">
                  <div className="wf-completion-message">
                    <i className="fas fa-check-circle" />
                    Todas las actividades han sido finalizadas
                  </div>
                  <button
                    className="btn btn-success btn-lg"
                    onClick={() => confirmTerminar.open()}
                  >
                    <i className="fas fa-file-signature" /> Terminar y generar informe
                  </button>
                </div>
              )}
            </div>
          </section>

        </div>
      </div>

      {/* Modal de confirmación para término */}
      <ConfirmDialog
        isOpen={confirmTerminar.state.open}
        title="Terminar OT y generar informe"
        message={
          <div>
            <p>Se completará la OT <strong>{ot.codigo_ot}</strong> y se generará un informe oficial firmado electrónicamente.</p>
            <div className="form-group">
              <label className="form-label">Observaciones de cierre (opcional)</label>
              <textarea
                className="form-textarea"
                value={observacionesCierre}
                onChange={e => setObservacionesCierre(e.target.value)}
                placeholder="Observaciones de cierre..."
                rows={3}
              />
            </div>
          </div>
        }
        confirmText="Firmar y terminar"
        variant="success"
        onConfirm={handleTerminar}
        onCancel={confirmTerminar.close}
        loading={confirmTerminar.state.loading}
      />

      {/* Modal de éxito tras término */}
      <ConfirmDialog
        isOpen={terminadoExito}
        title="OT completada exitosamente"
        message={
          <div>
            <p>La orden de trabajo <strong>{ot.codigo_ot}</strong> ha sido completada y el informe firmado ha sido generado.</p>
          </div>
        }
        confirmText="Descargar informe"
        cancelText="Cerrar"
        variant="success"
        onConfirm={handleDescargarInforme}
        onCancel={handleCloseExito}
      />

      <ConfirmDialog
        isOpen={iniciarAct.state.open}
        title="Iniciar Actividad"
        message={
          iniciarAct.state.item
            ? `¿Deseas iniciar la actividad "${iniciarAct.state.item.nombre}"? Se registrará la hora de inicio.`
            : ''
        }
        confirmText="Iniciar"
        onConfirm={handleConfirmActividad}
        onCancel={iniciarAct.close}
        variant="info"
        loading={iniciarAct.state.loading}
      />
    </>
  );
}

// ── Sub-components ──────────────────────────────────────────────

const CameraIcon = () => (
  <svg viewBox="0 0 24 24" strokeWidth="1.75" aria-hidden="true">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

/**
 * Item individual de la lista de actividades
 *
 * @param {Object} props
 * @param {Object} props.act - Datos de la actividad
 * @param {Function} props.onSelect - Callback al seleccionar la actividad
 * @returns {JSX.Element}
 */
function ActividadItem({ act, onSelect }) {
  const esCompletado = act.estado === 'completado';
  const esEnProgreso = act.estado === 'en_progreso';
  const esOmitido = act.estado === 'omitido';

  const antesCount = act.evidencia_antes_count || 0;
  const despuesCount = act.evidencia_despues_count || 0;
  const totalEvidencia = antesCount + despuesCount;
  const requiereEvidencia = act.requiere_evidencia;

  const handleClick = () => onSelect(act);

  return (
    <li
      className={`wf-act-item ${act.estado}`}
      tabIndex={0}
      role="button"
      aria-label={`Actividad ${act.orden}: ${act.nombre}, ${act.estado}`}
      onClick={handleClick}
      onKeyDown={(e) => { if (e.key === 'Enter') handleClick(); }}
    >
      <div className="wf-act-main">
        <span className={`actividad-orden ${act.estado}`} aria-hidden="true">
          {act.orden}
        </span>
        <div className="actividad-content">
          <div className={`actividad-nombre ${esCompletado || esOmitido ? 'tachado' : ''}`}>
            {act.nombre}
          </div>
          {act.descripcion && (
            <div className="wf-act-desc">{act.descripcion}</div>
          )}
        </div>

        <div className="wf-evi-chips" aria-label="Evidencias">
          {renderEvidenciaChips(act, requiereEvidencia, antesCount, despuesCount, totalEvidencia)}
        </div>

        <div className={`wf-status-icon ${act.estado}`} aria-hidden="true">
          {esCompletado && (
            <svg viewBox="0 0 24 24" strokeWidth="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          )}
          {esEnProgreso && (
            <svg viewBox="0 0 24 24" strokeWidth="2" className="wf-spin">
              <path d="M12 2a10 10 0 110 20A10 10 0 0112 2z" strokeDasharray="40 20" strokeLinecap="round"/>
            </svg>
          )}
          {esOmitido && (
            <svg viewBox="0 0 24 24" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          )}
          {act.estado === 'pendiente' && (
            <svg viewBox="0 0 24 24" strokeWidth="2">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          )}
        </div>
      </div>
    </li>
  );
}

/**
 * Renderiza los chips de evidencia según el tipo de actividad
 *
 * Si requiere evidencia antes/después, muestra chips separados.
 * Si no, muestra un chip con el conteo total.
 */
function renderEvidenciaChips(act, requiereEvidencia, antesCount, despuesCount, totalEvidencia) {
  // Actividad con evidencia antes/después (ej: cambiar filtro)
  if (requiereEvidencia && (act.estado === 'en_progreso' || act.estado === 'pendiente')) {
    return (
      <>
        {antesCount > 0 ? (
          <span className="wf-evi-chip evi-ok" title="Antes: capturada"><CameraIcon /> Antes</span>
        ) : (
          <span className="wf-evi-chip evi-pend" title="Antes: pendiente"><CameraIcon /> Antes</span>
        )}
        {despuesCount > 0 ? (
          <span className="wf-evi-chip evi-ok" title="Después: capturada"><CameraIcon /> Después</span>
        ) : (
          <span className="wf-evi-chip evi-pend" title="Después: pendiente"><CameraIcon /> Después</span>
        )}
      </>
    );
  }

  // Actividades completadas con evidencia capturada
  if (totalEvidencia > 0) {
    return (
      <span className="wf-evi-chip evi-ok" aria-label={`${totalEvidencia} evidencia${totalEvidencia !== 1 ? 's' : ''} capturada${totalEvidencia !== 1 ? 's' : ''}`}>
        <CameraIcon /> {totalEvidencia}
      </span>
    );
  }

  // Actividades pendientes con evidencia requerida pero sin capturar
  if (requiereEvidencia && act.estado === 'pendiente') {
    return (
      <span className="wf-evi-chip evi-pend" aria-label="Evidencia pendiente">
        <CameraIcon /> 1
      </span>
    );
  }

  return null;
}

export default DetalleOrdenTrabajo;
